import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  applyOpportunityBoardFilters,
  buildPipelineFromItems,
  moveOpportunityInBoard,
  OpportunityBoardFilterState,
  removeOpportunityFromBoard,
  upsertOpportunityInBoard,
} from '../../components/opportunities/opportunityBoardUtils';
import { OpportunityBoardFilters } from '../../components/opportunities/OpportunityBoardFilters';
import { OpportunityDetailPanel } from '../../components/opportunities/OpportunityDetailPanel';
import { OpportunityFormModal } from '../../components/opportunities/OpportunityFormModal';
import { OpportunityKanbanBoard } from '../../components/opportunities/OpportunityKanbanBoard';
import { PipelineSummaryBar } from '../../components/opportunities/PipelineSummaryBar';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  SalesOpportunity,
  SalesOpportunityListResult,
  SalesOpportunityTaskUpsertPayload,
  SalesOpportunityTeamMember,
  SalesOpportunityUpsertPayload,
  completeOpportunityTask,
  createOpportunityTask,
  stopOpportunityTaskRecurrence,
  createSalesOpportunity,
  deleteOpportunityTask,
  deleteSalesOpportunity,
  downloadOpportunityImportTemplate,
  getSalesOpportunity,
  getSalesOpportunityImportStatus,
  importSalesOpportunities,
  listOpportunityTeamMembers,
  listSalesOpportunities,
  syncSalesOpportunities,
  updateSalesOpportunity,
  updateSalesOpportunityStage,
} from '../../services/salesOpportunitiesApi';
import { MarketSegment, listMarketSegments } from '../../services/marketSegmentsApi';
import { OpportunityStageId } from '../../types/OpportunityStage';

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function OpportunitiesPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.opportunities');
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<SalesOpportunityListResult | null>(null);
  const [teamMembers, setTeamMembers] = useState<SalesOpportunityTeamMember[]>([]);
  const [marketSegments, setMarketSegments] = useState<MarketSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SalesOpportunity | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SalesOpportunity | null>(null);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [filters, setFilters] = useState<OpportunityBoardFilterState>({
    search: '',
    ownerUserId: '',
    stage: '',
  });
  const importInputRef = useRef<HTMLInputElement>(null);

  const displayData = useMemo(
    () => (data ? applyOpportunityBoardFilters(data, filters) : null),
    [data, filters],
  );

  const loadBoard = useCallback(async (silent = false): Promise<void> => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const [board, members, segmentsResult] = await Promise.all([
        listSalesOpportunities(),
        listOpportunityTeamMembers(),
        listMarketSegments({ activeOnly: true, page: 1, pageSize: 200 }),
      ]);
      setData(board);
      setTeamMembers(members);
      setMarketSegments(segmentsResult.items);
    } catch {
      toast.error('Falha ao carregar pipeline.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const syncBoard = useCallback(async (options?: { silent?: boolean }): Promise<boolean> => {
    const silent = options?.silent ?? false;
    setSyncing(true);
    try {
      const [board, members, segmentsResult] = await Promise.all([
        syncSalesOpportunities(),
        listOpportunityTeamMembers(),
        listMarketSegments({ activeOnly: true, page: 1, pageSize: 200 }),
      ]);
      setData(board);
      setTeamMembers(members);
      setMarketSegments(segmentsResult.items);
      if (!silent) {
        toast.success('Pipeline sincronizado.');
      }
      return true;
    } catch {
      toast.error('Falha ao sincronizar pipeline.');
      return false;
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    void loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    const opportunityId = searchParams.get('opportunityId');
    if (!opportunityId || loading) return;

    void (async () => {
      try {
        const opportunity = await getSalesOpportunity(opportunityId);
        setSelected(opportunity);
      } catch {
        toast.error('Oportunidade não encontrada.');
      }
    })();
  }, [searchParams, loading]);

  async function refreshSelected(opportunityId: string): Promise<void> {
    const opportunity = await getSalesOpportunity(opportunityId);
    setSelected(opportunity);
    setData((current) => (current ? upsertOpportunityInBoard(current, opportunity) : current));
  }

  async function handleMove(
    opportunityId: string,
    stage: OpportunityStageId,
    sortOrder: number,
  ): Promise<void> {
    if (!canWrite || !data) return;

    const snapshot = data;
    setData(moveOpportunityInBoard(data, opportunityId, stage, sortOrder));

    if (selected?.id === opportunityId) {
      setSelected((current) => (current ? { ...current, stage, sortOrder } : current));
    }

    try {
      const updated = await updateSalesOpportunityStage(opportunityId, { stage, sortOrder });
      setData((current) => (current ? upsertOpportunityInBoard(current, updated) : current));
      if (selected?.id === opportunityId) {
        setSelected(updated);
      }
    } catch {
      setData(snapshot);
      if (selected?.id === opportunityId) {
        const previous = snapshot.items.find((item) => item.id === opportunityId) ?? null;
        setSelected(previous);
      }
      toast.error('Falha ao mover oportunidade.');
    }
  }

  async function handleSelect(opportunity: SalesOpportunity): Promise<void> {
    setSelected(opportunity);
    setSearchParams({ opportunityId: opportunity.id });
    try {
      await refreshSelected(opportunity.id);
    } catch {
      toast.error('Falha ao carregar detalhes.');
    }
  }

  function handleCloseDetail(): void {
    setSelected(null);
    setSearchParams({});
  }

  async function handleSaveOpportunity(payload: SalesOpportunityUpsertPayload): Promise<void> {
    try {
      if (editing) {
        const updated = await updateSalesOpportunity(editing.id, payload);
        toast.success('Oportunidade atualizada.');
        setData((current) => (current ? upsertOpportunityInBoard(current, updated) : current));
        if (selected?.id === editing.id) {
          setSelected(updated);
        }
      } else {
        const created = await createSalesOpportunity(payload);
        toast.success('Oportunidade criada.');
        setData((current) =>
          current
            ? upsertOpportunityInBoard(current, created)
            : { items: [created], pipeline: buildPipelineFromItems([created]) },
        );
      }
    } catch {
      toast.error('Falha ao salvar oportunidade.');
      throw new Error('save failed');
    }
  }

  async function handleDeleteOpportunity(): Promise<void> {
    if (!selected || !canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir oportunidade',
        message: `Excluir a oportunidade "${selected.title}"? Esta ação remove o card do pipeline.`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteSalesOpportunity(selected.id);
      setData((current) => (current ? removeOpportunityFromBoard(current, selected.id) : current));
      handleCloseDetail();
      toast.success('Oportunidade excluída.');
    } catch {
      toast.error('Falha ao excluir oportunidade.');
    }
  }

  async function handleCreateTask(payload: SalesOpportunityTaskUpsertPayload): Promise<void> {
    if (!selected) return;
    try {
      await createOpportunityTask(selected.id, payload);
      toast.success(
        payload.recurrenceType && payload.recurrenceType !== 'None'
          ? 'Tarefas recorrentes criadas.'
          : 'Tarefa criada.',
      );
      await refreshSelected(selected.id);
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail || 'Falha ao criar tarefa.');
    }
  }

  async function handleStopTaskRecurrence(taskId: string): Promise<void> {
    if (!selected) return;
    if (
      !(await confirm({
        title: 'Encerrar recorrência',
        message: 'As tarefas já criadas permanecem. Nenhuma ocorrência nova será gerada.',
        confirmLabel: 'Encerrar',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await stopOpportunityTaskRecurrence(taskId);
      toast.success('Recorrência encerrada.');
      await refreshSelected(selected.id);
    } catch {
      toast.error('Falha ao encerrar a recorrência.');
    }
  }

  async function handleCompleteTask(taskId: string): Promise<void> {
    if (!selected) return;
    try {
      await completeOpportunityTask(taskId);
      toast.success('Tarefa concluída.');
      await refreshSelected(selected.id);
    } catch {
      toast.error('Falha ao concluir tarefa.');
    }
  }

  async function handleDeleteTask(taskId: string): Promise<void> {
    if (!selected) return;
    if (
      !(await confirm({
        title: 'Excluir tarefa',
        message: 'Excluir esta tarefa?',
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await deleteOpportunityTask(taskId);
      toast.success('Tarefa excluída.');
      await refreshSelected(selected.id);
    } catch {
      toast.error('Falha ao excluir tarefa.');
    }
  }

  async function handleDownloadTemplate(): Promise<void> {
    try {
      const blob = await downloadOpportunityImportTemplate();
      triggerBlobDownload(blob, 'baruk-go-oportunidades-template.csv');
      toast.success('Template baixado.');
    } catch {
      toast.error('Falha ao baixar template.');
    }
  }

  async function pollImportStatus(importId: string): Promise<void> {
    for (let attempt = 0; attempt < 30; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const status = await getSalesOpportunityImportStatus(importId);
        if (status.status === 'Completed') {
          toast.success(
            `Importação concluída: ${status.createdCount} criadas, ${status.updatedCount} atualizadas, ${status.companiesCreatedCount} empresas.`,
          );
          await syncBoard({ silent: true });
          return;
        }
        if (status.status === 'Failed') {
          toast.error(status.errorSummary || 'Importação falhou.');
          return;
        }
      } catch {
        // Continua tentando enquanto o job processa.
      }
    }
    toast.info('Importação ainda em andamento. Use Sincronizar quando concluir.');
  }

  async function handleImportFile(file: File | undefined): Promise<void> {
    if (!file || !canWrite) return;
    setImporting(true);
    try {
      const queued = await importSalesOpportunities(file);
      toast.success(
        queued.hangfireJobId
          ? `Importação enfileirada (Hangfire ${queued.hangfireJobId}).`
          : 'Importação enfileirada.',
      );
      void pollImportStatus(queued.importId);
    } catch {
      toast.error('Falha ao enviar arquivo de importação.');
    } finally {
      setImporting(false);
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  }

  if (loading || !data || !displayData) {
    return <LoadingSpinner label="Carregando pipeline..." />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Pipeline comercial"
        description="Visão Kanban das oportunidades por estágio, com valor consolidado e tarefas com lembretes."
        actions={
          <div className="erp-btn-actions">
            <button
              type="button"
              className="erp-btn-secondary inline-flex items-center gap-1.5"
              disabled={syncing}
              title="Limpar cache do pipeline e recarregar do banco"
              onClick={() => void syncBoard()}
            >
              <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </button>
            <button
              type="button"
              className="erp-btn-secondary"
              onClick={() => void handleDownloadTemplate()}
            >
              Template CSV
            </button>
            {canWrite ? (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(event) => void handleImportFile(event.target.files?.[0])}
                />
                <button
                  type="button"
                  className="erp-btn-secondary"
                  disabled={importing}
                  onClick={() => importInputRef.current?.click()}
                >
                  {importing ? 'Importando...' : 'Importar'}
                </button>
                <button
                  type="button"
                  className="erp-btn-primary"
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                >
                  Nova oportunidade
                </button>
              </>
            ) : null}
          </div>
        }
      />

      <OpportunityBoardFilters
        filters={filters}
        teamMembers={teamMembers}
        onChange={setFilters}
      />

      <PipelineSummaryBar pipeline={displayData.pipeline} />

      <OpportunityKanbanBoard
        items={displayData.items}
        onSelect={(opportunity) => void handleSelect(opportunity)}
        onMove={(id, stage, sortOrder) => void handleMove(id, stage, sortOrder)}
      />

      <OpportunityDetailPanel
        opportunity={selected}
        teamMembers={teamMembers}
        canWrite={canWrite}
        onClose={handleCloseDetail}
        onEdit={() => {
          if (!selected) return;
          setEditing(selected);
          setFormOpen(true);
        }}
        onDelete={handleDeleteOpportunity}
        onCreateTask={handleCreateTask}
        onCompleteTask={handleCompleteTask}
        onDeleteTask={handleDeleteTask}
        onStopRecurrence={handleStopTaskRecurrence}
      />

      <OpportunityFormModal
        open={formOpen}
        opportunity={editing}
        teamMembers={teamMembers}
        marketSegments={marketSegments}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSaveOpportunity}
      />
    </PageShell>
  );
}
